import React, { useState, useMemo } from 'react';
import { useTheme } from 'styled-components';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import { ButtonWrapper } from './styles';


const formatDefaultIconName = (name) =>
  name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');


/**
 * Wrapper for Icon component to support interactions 
 * (click, hover/press, background, tooltip, keyboard support)
 */
const IconButtonV2 = ({
  type,
  size = 'md',
  color,
  selectedColor,
  selected = false,
  disabled = false,
  onClick,
  label,
  selectedBackground = true,
  hoverBackground = true,
  defaultBackground,
  tooltip = false,
  tooltipText,
  style: styleProp = {},
  iconStyle: iconStyleProp = {},
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const isInteractive = !!onClick && !disabled;

  // set background defaults
  const resolvedHoverBg = hoverBackground === true
    ? theme.backgroundLight
    : (hoverBackground || null);
  const resolvedSelectedBg = selectedBackground === true
    ? theme.backgroundLighter
    : (selectedBackground || null);
  const hasBackground = !!(resolvedHoverBg || resolvedSelectedBg);

  const isSelectedHovered = selected && isHovered && isInteractive && !!resolvedSelectedBg;

  // set icon color defaults
  const resolvedColor = useMemo(() => {
    if (disabled) return theme.grey3;
    if (selected) return selectedColor ?? theme.primary;
    if (isHovered && isInteractive) return color ?? theme.textLight;
    return color ?? theme.textMedium;
  }, [disabled, selected, isHovered, isInteractive, color, selectedColor, theme]);

  const resolvedBackground = useMemo(() => {
    if (selected && resolvedSelectedBg) return resolvedSelectedBg;
    if (isHovered && isInteractive && resolvedHoverBg) return resolvedHoverBg;
    return defaultBackground || 'transparent';
  }, [selected, isHovered, isInteractive, resolvedSelectedBg, resolvedHoverBg, defaultBackground]);

  // press effect
  const resolvedTransform = useMemo(() => {
    if (isPressed && isInteractive) return 'scale(0.92)';
    if (isHovered && isInteractive) return 'scale(1.08)';
    return 'scale(1)';
  }, [isPressed, isHovered, isInteractive]);

  // brightness controls
  const resolvedFilter = useMemo(() => {
    if (isPressed && isInteractive) return 'brightness(0.75)';
    if (isSelectedHovered) return 'brightness(1.25)';
    return 'none';
  }, [isPressed, isSelectedHovered, isInteractive]);

  const wrapperStyle = {
    background: resolvedBackground,
    borderRadius: hasBackground ? theme.br.sm : undefined,
    padding: hasBackground ? theme.spacing[2] : undefined,
    transition: 'all 0.18s ease',
    transform: resolvedTransform,
    filter: resolvedFilter,
    opacity: disabled ? 0.45 : 1,
    ...styleProp,
  };

  const buttonEl = (
    <ButtonWrapper
      $disabled={disabled}
      style={wrapperStyle}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick(e);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      <Icon
        type={type}
        size={size}
        color={resolvedColor}
        selected={selected}
        style={iconStyleProp}
      />
    </ButtonWrapper>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltipText ?? formatDefaultIconName(type)}>
        {buttonEl}
      </Tooltip>
    );
  }

  return buttonEl;
};

export default IconButtonV2;
